import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import Alert from '../../components/Alert/Alert';
import api from '../../api';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthProvider';
import { formatDateDDMMYYYY } from '../../utils/formatters';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';

const DEFAULT_PAGE_SIZE = 10;

// Map frontend column keys to backend sort property names (Spring sort param) — backend-based
const PROSPECTS_REPORT_SORT_KEY_TO_BACKEND = {
  companyName: 'companyName',
  projectCount: 'projectCount',
  leadsCount: 'leadsCount',
  contactName: 'contactName',
  phone: 'phone',
  email: 'email',
  sector: 'sectorName',
  createdBy: 'createdByName',
  departmentName: 'departmentName',
  companyCreatedDate: 'companyCreatedDate',
  lastUpdateDate: 'companyUpdatedDate',
};

const getEmptyProspectReportFilters = () => ({
  period: '',
  startDate: '',
  endDate: '',
  createdBy: [],
  department: [],
  sector: [],
  standard: [],
  source: [],
  service: [],
  stage: [],
  branchId: [],
  taggingIds: [],
  filterType: '',
});

const ProspectsReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadPersistedState, persistState } = usePersistedFilters('prospects_report');
  const { user, can, capabilitiesLoading } = useAuth();
  const canProspect = (capabilityKey) => can(capabilityKey, 'prospect');

  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [prospectsData, setProspectsData] = useState([]);

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Server-side sort
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // in-progress (panel open) vs committed (applied to query)
  const [filterData, setFilterData] = useState(getEmptyProspectReportFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyProspectReportFilters);

  // Filter options
  const [sourcesData, setSourcesData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [sectorsData, setSectorsData] = useState([]);
  const [standardsData, setStandardsData] = useState([]);
  const [businessUnitsData, setBusinessUnitsData] = useState([]);
  const [salesUsersData, setSalesUsersData] = useState([]);
  const [stagesData, setStagesData] = useState([]);
  // HRIS user status lookup for "(Inactive)" badge
  const [prospectRptUserStatusMap, setProspectRptUserStatusMap] = useState(new Map());
  useEffect(() => {
    api.get('/md/internal-users/option-internal-user')
      .then(res => {
        const map = new Map();
        (res.data || []).forEach(u => { if (u.id != null) map.set(String(u.id), u.status); });
        setProspectRptUserStatusMap(map);
      })
      .catch(() => {});
  }, []);
  const [branchesData, setBranchesData] = useState([]);
  const [taggingData, setTaggingData] = useState([]);

  // Track if URL params have been processed to prevent loading before URL params are read
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  // Determine which filters to show based on permissions
  const getEnabledFilters = () => {
    if (canProspect('view_global')) {
      return ['createdBy', 'department', 'sector', 'standard', 'source', 'service', 'stage', 'branchId', 'taggingIds', 'period'];
    } else if (canProspect('view_department')) {
      return ['createdBy', 'department', 'sector', 'standard', 'source', 'service', 'stage', 'branchId', 'taggingIds', 'period'];
    } else if (canProspect('view_own')) {
      return ['sector', 'standard', 'source', 'service', 'stage', 'branchId', 'taggingIds', 'period'];
    }
    return ['sector', 'standard', 'source', 'service', 'stage', 'branchId', 'taggingIds', 'period'];
  };

  const getFilterOverrides = () => {
    if (canProspect('view_department') && !canProspect('view_global')) {
      return {
        department: user?.departmentId ? [String(user.departmentId)] : [],
        isDepartmentLocked: true,
        filterCreatedByDepartment: user?.departmentId ? String(user.departmentId) : null
      };
    }
    return {};
  };

  // Fetch filter options
  const fetchSourcesData = async () => {
    try {
      const response = await api.get('/dashboard/sources');
      if (response.data && response.data.sources) setSourcesData(response.data.sources);
    } catch (error) {
      console.error('Error fetching sources data:', error);
      setSourcesData([]);
    }
  };

  const fetchSectorsData = async () => {
    try {
      const response = await api.get('/dashboard/sectors');
      if (response.data && response.data.sectors) setSectorsData(response.data.sectors);
    } catch (error) {
      console.error('Error fetching sectors data:', error);
      setSectorsData([]);
    }
  };

  const fetchServicesData = async () => {
    try {
      const response = await api.get('/crm/services/options');
      setServicesData((response.data || []).map(s => ({ id: s.id ?? s.value, name: s.name ?? s.label })));
    } catch (error) {
      console.error('Error fetching services data:', error);
      setServicesData([]);
    }
  };

  const fetchStandardsData = async () => {
    try {
      const response = await api.get('/dashboard/standards');
      if (response.data && response.data.standards) setStandardsData(response.data.standards);
    } catch (error) {
      console.error('Error fetching standards data:', error);
      setStandardsData([]);
    }
  };

  const fetchBusinessUnitsData = async () => {
    try {
      const response = await api.get('/md/departement');
      setBusinessUnitsData((response.data || []).map(d => ({ id: String(d.id), name: d.name })));
    } catch (error) {
      console.error('Error fetching business units data:', error);
      setBusinessUnitsData([]);
    }
  };

  const fetchSalesUsersData = async () => {
    try {
      const response = await api.get('/dashboard/sales-users');
      if (response.data && response.data.salesUsers) {
        let users = response.data.salesUsers;
        const overrides = getFilterOverrides();
        if (overrides.filterCreatedByDepartment) {
          users = users.filter(u => {
            const deptId = u.departmentId;
            return deptId !== null && deptId !== undefined &&
                   String(deptId) === String(overrides.filterCreatedByDepartment);
          });
        }
        setSalesUsersData(users);
      }
    } catch (error) {
      console.error('Error fetching sales users data:', error);
      setSalesUsersData([]);
    }
  };

  const fetchStagesData = async () => {
    try {
      const response = await api.get('/crm/lead-statuses/options');
      const stages = response.data || [];
      setStagesData(stages.map(s => ({ id: s.name, name: s.name })));
    } catch (error) {
      console.error('Error fetching stages data:', error);
    }
  };

  const fetchBranchesData = async () => {
    try {
      const response = await api.get('/branches');
      const data = response.data?.data ?? response.data ?? [];
      setBranchesData(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching branches data:', error);
    }
  };

  const fetchTaggingData = async () => {
    try {
      const response = await api.get('/md/tagging', { params: { moduleKey: 'companies' } });
      setTaggingData(response.data || []);
    } catch (error) {
      console.error('Error fetching tagging data:', error);
    }
  };

  // Load filter options on mount
  useEffect(() => {
    const enabledFilters = getEnabledFilters();
    if (enabledFilters.includes('source')) fetchSourcesData();
    if (enabledFilters.includes('service')) fetchServicesData();
    if (enabledFilters.includes('sector')) fetchSectorsData();
    if (enabledFilters.includes('standard')) fetchStandardsData();
    if (enabledFilters.includes('department')) fetchBusinessUnitsData();
    if (enabledFilters.includes('createdBy')) fetchSalesUsersData();
    if (enabledFilters.includes('stage')) fetchStagesData();
    if (enabledFilters.includes('branchId')) fetchBranchesData();
    if (enabledFilters.includes('taggingIds')) fetchTaggingData();
  }, [user?.departmentId]);

  const parseParamToArray = (v) => {
    if (!v || v === 'all') return [];
    return v.split(',').map((s) => s.trim()).filter(Boolean);
  };

  // Initialize filters from URL query parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const hasUrlParams = Array.from(searchParams.keys()).length > 0;

    if (hasUrlParams) {
      const urlPeriod = searchParams.get('period') || '';
      const urlCustomStart = searchParams.get('customStartDate') || '';
      const urlCustomEnd = searchParams.get('customEndDate') || '';
      const urlFilterType = searchParams.get('filterType') || '';

      const urlFilters = {
        period: urlPeriod === 'all' ? '' : urlPeriod,
        startDate: urlPeriod === 'custom' ? urlCustomStart : '',
        endDate: urlPeriod === 'custom' ? urlCustomEnd : '',
        createdBy: parseParamToArray(searchParams.get('createdBy') || searchParams.get('people')),
        department: parseParamToArray(searchParams.get('department') || searchParams.get('businessUnits')),
        sector: parseParamToArray(searchParams.get('sector')),
        standard: parseParamToArray(searchParams.get('standard')),
        source: parseParamToArray(searchParams.get('source')),
        service: parseParamToArray(searchParams.get('service')),
        stage: parseParamToArray(searchParams.get('stage')),
        branchId: parseParamToArray(searchParams.get('branchId')),
        taggingIds: parseParamToArray(searchParams.get('taggingIds')),
        filterType: urlFilterType === 'new' ? 'new' : '',
      };

      setFilterData(urlFilters);
      setAppliedFilters(urlFilters);
    } else {
      // No URL params — restore from sessionStorage if available
      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyProspectReportFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;
        setFilterData(restoredFilters);
        setAppliedFilters(restoredFilters);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);
      } else {
        // Apply department restriction for view_department users
        if (!capabilitiesLoading && canProspect('view_department') && !canProspect('view_global') && user?.departmentId) {
          const restricted = {
            ...getEmptyProspectReportFilters(),
            department: [String(user.departmentId)],
          };
          setFilterData(restricted);
          setAppliedFilters(restricted);
        }
      }
    }

    setUrlParamsProcessed(true);
  }, [location.search]);

  // Apply department restriction when capabilities resolve (no URL params case)
  useEffect(() => {
    if (!capabilitiesLoading && urlParamsProcessed) {
      const searchParams = new URLSearchParams(location.search);
      const hasUrlParams = Array.from(searchParams.keys()).length > 0;
      if (!hasUrlParams && canProspect('view_department') && !canProspect('view_global') && user?.departmentId) {
        setAppliedFilters(prev => {
          if (prev.department.length === 0) {
            const updated = { ...prev, department: [String(user.departmentId)] };
            setFilterData(updated);
            return updated;
          }
          return prev;
        });
      }
    }
  }, [capabilitiesLoading, user?.departmentId, urlParamsProcessed]);

  // Reset to page 0 when appliedFilters change
  useEffect(() => {
    setPage(0);
  }, [appliedFilters]);

  // Reload data when capabilities/URL are ready, or when page/size/sort/search or appliedFilters change
  useEffect(() => {
    if (!capabilitiesLoading && urlParamsProcessed) {
      loadProspects(page, size, sortConfig);
    }
  }, [capabilitiesLoading, urlParamsProcessed, page, size, sortConfig, searchQuery, appliedFilters]);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000);
  };

  const loadProspects = async (pageNum = page, pageSize = size, sortConfigOverride = null) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('size', String(pageSize));

      // Resolve period → startDate/endDate
      const { startDate, endDate } = getActivePeriodRange(appliedFilters);

      // Multi-value array filters → comma-separated backend params
      const toParam = (arr) => {
        const clean = (arr || []).filter(v => v != null && v !== '' && v !== 'all');
        return clean.length > 0 ? clean.join(',') : null;
      };

      const createdByParam = toParam(appliedFilters.createdBy);
      const departmentParam = toParam(appliedFilters.department);
      const sectorParam = toParam(appliedFilters.sector);
      const standardParam = toParam(appliedFilters.standard);
      const sourceParam = toParam(appliedFilters.source);
      const serviceParam = toParam(appliedFilters.service);
      const stageParam = toParam(appliedFilters.stage);
      const branchIdParam = toParam(appliedFilters.branchId);
      const taggingIdsParam = toParam(appliedFilters.taggingIds);

      if (createdByParam) params.append('createdBy', createdByParam);
      if (departmentParam) params.append('department', departmentParam);
      if (sectorParam) params.append('sector', sectorParam);
      if (standardParam) params.append('standards', standardParam);
      if (sourceParam) params.append('source', sourceParam);
      if (serviceParam) params.append('service', serviceParam);
      if (stageParam) params.append('stage', stageParam);
      if (branchIdParam) params.append('branchId', branchIdParam);
      if (taggingIdsParam) params.append('taggingIds', taggingIdsParam);
      if (searchQuery && searchQuery.trim()) params.append('search', searchQuery.trim());
      if (appliedFilters.filterType === 'new') params.append('filterType', 'new');

      if (startDate) params.append('dateFrom', startDate);
      if (endDate) params.append('dateTo', endDate);

      // Permission-based fallback when no explicit filter is set
      if (!canProspect('view_global')) {
        if (canProspect('view_department')) {
          if (!createdByParam && !departmentParam && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (canProspect('view_own')) {
          if (!createdByParam && user?.id) {
            params.append('createdBy', String(user.id));
          }
        }
      }

      // Server-side sort
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && PROSPECTS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        params.set('sort', `${PROSPECTS_REPORT_SORT_KEY_TO_BACKEND[effectiveSort.key]},${effectiveSort.direction}`);
      }

      const hasFilters = [...params.keys()].some(k => k !== 'page' && k !== 'size' && k !== 'sort');
      const url = hasFilters ? `/crm/prospects/filter?${params.toString()}` : `/crm/prospects?${params.toString()}`;

      const response = await api.get(url);
      const data = response.data;
      const list = Array.isArray(data) ? data : (data?.content ?? data?.data ?? []);

      const isPagedResponse = data && typeof data.totalElements === 'number';
      if (isPagedResponse) {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }

      const formattedData = list.map(item => ({
        ...item,
        projectCount: item.projectCount || 0,
        leadsCount: item.leadsCount || 0,
        contactName: item.contactName,
        phone: item.phone,
        sector: item.sectorName,
        email: item.email,
        assignedUser: item.assignedUserName || 'Unassigned',
        createdBy: item.createdByName || item.createdBy || item.userName || item.user?.name ||
                   item.createdUser?.name || item.createdByUser?.name || item.companyCreatedBy ||
                   item.createdByUserName || '-',
        departmentName: item.departmentName || '-',
        companyCreatedDate: item.companyCreatedDate || '-',
        lastUpdateDate: item.lastUpdateDate || item.companyUpdatedDate || item.udate || '-',
        lastUpdateBy: item.lastUpdateByName || item.lastUpdateBy || item.updatedByName ||
                      item.updatedBy || item.updateUserName || item.updateUser?.name ||
                      item.updatedUser?.name || item.lastUpdatedByUserName || '-',
      }));

      setProspectsData(formattedData);
    } catch (error) {
      console.error('Error loading prospects:', error);
      showAlert('danger', 'Failed to load prospects');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Filter handlers (AdvancedFilterPanel pattern) ─────────────────────────

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: searchQuery, page: 0, size });
    // loadProspects will fire via useEffect when appliedFilters changes
  };

  const handleFilterReset = () => {
    const overrides = getFilterOverrides();
    const empty = {
      ...getEmptyProspectReportFilters(),
      department: overrides.department ?? [],
    };
    setFilterData(empty);
    setAppliedFilters(empty);
    setPage(0);
    persistState({ filters: empty, search: '', page: 0, size });
  };

  // ── Filter fields config for AdvancedFilterPanel ──────────────────────────

  const enabledFilters = getEnabledFilters();
  const overrides = getFilterOverrides();

  // Cascade: filter Created By by selected department(s), mark inactive
  const cascadedProspectCreatedBy = useMemo(() => {
    return salesUsersData.map(u => {
      const label = prospectRptUserStatusMap.get(String(u.id)) === '0' ? `${u.name} (Inactive)` : u.name;
      return { value: String(u.id), label };
    });
  }, [salesUsersData, prospectRptUserStatusMap]);

  const filterFields = useMemo(() => {
    const fields = [];

    if (enabledFilters.includes('department')) {
      fields.push({
        key: 'department',
        label: 'Department',
        type: 'multiselect',
        options: businessUnitsData.map(d => ({ value: String(d.id), label: d.name })),
        disabled: overrides.isDepartmentLocked,
      });
    }

    if (enabledFilters.includes('createdBy')) {
      fields.push({
        key: 'createdBy',
        label: 'Created By',
        type: 'multiselect',
        options: cascadedProspectCreatedBy,
      });
    }

    if (enabledFilters.includes('sector')) {
      fields.push({
        key: 'sector',
        label: 'Sector',
        type: 'multiselect',
        options: sectorsData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('standard')) {
      fields.push({
        key: 'standard',
        label: 'Standard',
        type: 'multiselect',
        options: standardsData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('source')) {
      fields.push({
        key: 'source',
        label: 'Source',
        type: 'multiselect',
        options: sourcesData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('service')) {
      fields.push({
        key: 'service',
        label: 'Service',
        type: 'multiselect',
        options: servicesData.map(s => ({ value: String(s.id), label: s.name })),
      });
    }

    if (enabledFilters.includes('stage')) {
      fields.push({
        key: 'stage',
        label: 'Stage',
        type: 'multiselect',
        options: stagesData.map(s => ({ value: s.id, label: s.name })),
      });
    }

    if (enabledFilters.includes('branchId')) {
      fields.push({
        key: 'branchId',
        label: 'Branch',
        type: 'multiselect',
        options: branchesData.map(b => ({ value: String(b.id), label: b.name })),
      });
    }

    if (enabledFilters.includes('taggingIds')) {
      fields.push({
        key: 'taggingIds',
        label: 'Tagging',
        type: 'multiselect',
        options: taggingData.map(t => ({ value: String(t.id), label: t.name })),
      });
    }

    // "New companies" toggle as a select
    fields.push({
      key: 'filterType',
      label: 'Company Type',
      type: 'select',
      options: [{ value: 'new', label: 'New companies (no leads)' }],
    });

    return fields;
  }, [salesUsersData, businessUnitsData, sectorsData, standardsData, sourcesData, servicesData, stagesData, branchesData, taggingData, cascadedProspectCreatedBy, enabledFilters.join(','), overrides.isDepartmentLocked]);

  // ── Active filter count & tags ─────────────────────────────────────────────

  const activeFilterCount = getActiveFilterCount(appliedFilters, filterFields);

  const filterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    filterFields.forEach((field) => {
      if (field.type === 'dateRange') return;
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined ||
                      (Array.isArray(value) && value.length === 0);
      if (isEmpty) return;

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find(opt => String(opt.value) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option.label : value}` });
    });

    return tags;
  }, [appliedFilters, filterFields]);

  // ── Chip removal ──────────────────────────────────────────────────────────

  const handleChipRemove = (key) => {
    const periodKeys = ['period', 'startDate', 'endDate'];
    const clearKeys = periodKeys.includes(key) ? periodKeys : [key];

    const nextFilters = { ...appliedFilters };
    clearKeys.forEach(k => {
      nextFilters[k] = Array.isArray(appliedFilters[k]) ? [] : '';
    });

    setFilterData(nextFilters);
    setAppliedFilters(nextFilters);
    setPage(0);
    persistState({ filters: nextFilters, search: searchQuery, page: 0, size });
  };

  // ── Excel export ──────────────────────────────────────────────────────────

  const handleExportExcel = () => {
    if (!canProspect('export_data')) {
      showAlert('danger', 'You do not have permission to export data.');
      return;
    }
    try {
      const dataToExport = searchQuery
        ? prospectsData.filter(item =>
            item.companyName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.contactName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.email?.toLowerCase().includes(searchQuery.toLowerCase())
          )
        : prospectsData;

      const excelData = dataToExport.map(row => ({
        'Company Name': row.companyName || '',
        'Projects': row.projectCount || 0,
        'Leads': row.leadsCount || 0,
        'Contact Name': row.contactName || '',
        'Contact No.': row.phone || '',
        'Contact Email': row.email || '',
        'Sector': row.sector || '',
        'Standard': row.standardName || row.standard || '',
        'Created By': row.createdBy || '',
        'Department': row.departmentName || '',
        'Create Date': row.companyCreatedDate ? formatDateDDMMYYYY(row.companyCreatedDate) : '-',
        'Last Update': row.lastUpdateDate ? formatDateDDMMYYYY(row.lastUpdateDate) : '-',
        'Update By': row.lastUpdateBy || '-',
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      worksheet['!cols'] = [
        { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 25 }, { wch: 15 },
        { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 },
        { wch: 15 }, { wch: 15 }, { wch: 20 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Prospects Report');

      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      XLSX.writeFile(workbook, `prospects_report_${timestamp}.xlsx`);

      showAlert('success', 'Prospects report exported to Excel successfully!');
    } catch (e) {
      console.error('Export Excel failed:', e);
      showAlert('danger', 'Failed to export prospects report to Excel. Please try again.');
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────

  const columns = [
    {
      key: 'companyName',
      label: 'Company Name',
      render: (value, row) => (
        <span
          className="text-primary"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/clients/${row.companyId || row.id}`)}
          role="button"
          tabIndex="0"
          aria-label={`View details for ${value}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              navigate(`/clients/${row.companyId || row.id}`);
            }
          }}
        >
          {value}
        </span>
      ),
    },
    { 
      key: 'tagging',
      label: 'Tagging',
      render: (value) => {
        if (!value) return <span className="text-muted">-</span>;
        const tags = typeof value === 'string' ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) return <span className="text-muted">-</span>;
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span key={i} className="badge bg-blue-lt text-wrap" title={tag} style={{ fontSize: '0.7rem' }}>
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    { key: 'projectCount', label: 'Projects', type: 'number' },
    { key: 'leadsCount', label: 'Leads', type: 'number' },
    { key: 'contactName', label: 'Contact Name' },
    { key: 'phone', label: 'Contact No.' },
    { key: 'email', label: 'Contact Email' },
    { key: 'sector', label: 'Sector' },
    { key: 'standardName', label: 'Standard' },
    { key: 'createdBy', label: 'Created By' },
    { key: 'departmentName', label: 'Department' },
    {
      key: 'companyCreatedDate',
      label: 'Create Date',
      type: 'date',
      render: (value) => {
        if (!value || value === '-') return '-';
        try { return formatDateDDMMYYYY(value); } catch (e) { return value; }
      },
    },
    {
      key: 'lastUpdateDate',
      label: 'Last Update',
      type: 'date',
      render: (value) => {
        if (!value || value === '-') return '-';
        try { return formatDateDDMMYYYY(value); } catch (e) { return value; }
      },
    },
    {
      key: 'lastUpdateBy',
      label: 'Update By',
      render: (value) => value || '-',
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col-md-3">
          <h3 className="page-title m-0">Prospects Reports</h3>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">
                      List Data
                    </a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={prospectsData}
                      columns={columns}
                      isLoading={isLoading}
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onSearchSubmit={() => setPage(0)}
                          placeholder="Search prospects..."
                        />
                      }
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasProspectsReport" activeCount={activeFilterCount} />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canProspect('export_data') ? handleExportExcel : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Prospects Report is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={filterTags.map(tag => tag.label).join(' · ')}
                          loading={isLoading}
                          activeCount={activeFilterCount}
                          filterTags={filterTags}
                          onRemoveFilter={handleChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: searchQuery, page: nextPage, size });
                          loadProspects(nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: searchQuery, page: 0, size: newSize });
                          loadProspects(0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        loadProspects(0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Unified AdvancedFilterPanel — period at TOP, uniform design */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasProspectsReport"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
      />
    </div>
  );
};

export default ProspectsReports;
